<template>
  <CloseHeader @close="goToMain" />

  <div class="app-container one-page">
    <div class="fst-step-con">
      <div class="gauge-bar">
        <span class="bar-bg"></span>
        <span class="active"></span>
      </div>
      <div class="inr-con">
        <h1>
          <i18n-t keypath="select_name_title">
            <template #name>
              <span class="primary">{{ t('label_name') }}</span>
            </template>
            <template #nickname>
              <span class="primary">{{ t('entrance_intro_message_2') }}</span>
            </template>
          </i18n-t>
        </h1>
        <input
          type="text"
          class="app-t-input"
          enterkeyhint="done"
          :value="updatedNickname"
          :placeholder="t('name_or_nickname')"
          v-cursor-end
          @input="handleNickname"
          @keydown.enter.prevent="(e) => (e.target as HTMLInputElement).blur()"
        />
      </div>
    </div>
    <div class="app-btn-wrap">
      <button
        :disabled="updatedNickname.length < 2 || isLoading"
        type="button"
        class="primary-btn l-btn"
        @click="goToNextStep"
      >
        <span>{{ t('next') }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';

import { updateEmployee } from '@/api/apiService';
import CloseHeader from '@/components/domains/mobile/common/CloseHeader.vue';
import { useNotification } from '@/composables/shared/useNotification';
import { useUserStore } from '@/stores/user';

const userStore = useUserStore();

const router = useRouter();
const { t } = useI18n();
const { alertNetworkError } = useNotification();

const updatedNickname = ref<string>('');
const isLoading = ref<boolean>(false);

const handleNickname = (e) => {
  updatedNickname.value = e.target.value;
};

const goToMain = () => {
  router.replace({ name: 'app-main-view' });
};

const goToNextStep = async () => {
  const success = await requestChangeNickname();

  if (success) {
    await router.replace({ name: 'app-onboarding-step3' });
  }
};

const requestChangeNickname = async (): Promise<boolean> => {
  let isSuccess = false;
  isLoading.value = true;
  const { nickname, userId } = userStore.userInfo || {};

  if (nickname !== updatedNickname.value) {
    try {
      const res = await updateEmployee(userId, null, {
        body: JSON.stringify({
          nickname: updatedNickname.value,
          languageCode: userStore.userInfo.languageCode,
          groupIds: [],
          sampleRate: '16000',
        }),
        voice: '',
      });

      if (!res.success) {
        throw new Error();
      }

      isSuccess = true;
    } catch (error) {
      alertNetworkError();
      console.error(error);
    }
  } else {
    isSuccess = true;
  }
  isLoading.value = false;

  return isSuccess;
};

onMounted(async () => {
  await userStore.fetchUserInfo();
  updatedNickname.value = userStore.userInfo?.nickname ?? '';
});
</script>

<style scoped lang="scss">
.fst-step-con {
  .gauge-bar {
    position: relative;
    padding: 1rem 0;
    .bar-bg {
      display: block;
      background: $gray10;
      border-radius: 100px;
      height: $size-s;
      width: 100%;
    }
    .active {
      background: $color-gray-900;
      width: 66.6%;
      height: $size-s;
      position: absolute;
      left: 0;
      top: 1rem;
      border-radius: 100px;
    }
  }
  .inr-con {
    h1 {
      font-size: 1.5rem;
      padding: 2rem 0 1.5rem;
      line-height: 130%;
      span {
        font-size: 1.5rem;
        line-height: 130%;
      }
    }
  }
}
.app-btn-wrap {
  padding-bottom: 1.25rem;
  button {
    width: 100%;
  }
}
</style>
